import React, { useState } from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    StudentPersonalInfoViewModel,
    Organization,
    FormSelectChoice,
    StudentPersonalInfoResponse,
    Locale,
    VerificationStepsEnum,
    hasFailedInstantMatch,
    isFormFilled,
    setRef,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableLocaleChoices,
    ChangeLocaleComponent,
    MarketConsentWrapper,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    FormFooterComponent as FormFooter,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    CountryComponentWrapper,
    RewardsRemainingComponent,
    ExtendedFieldValidationErrors,
    FieldValidationErrors,
} from '@sheerid/jslib';

import { ParentalConsentComponent } from '../parentalConsentComponent/ParentalConsentComponent';
import { calcAge as usersAgeInYears } from '../../lib/utils/helperFunctions';
import { parentalConsentMinAge } from '../../constants';

interface StepStudentPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({
    intl,
    verificationService,
}: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    // eslint-disable-next-line prefer-destructuring
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(
        verificationService.programTheme,
        intl,
    );
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = {
        value: 'en-US',
        label: 'English',
    };
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const [parentalConsentRequired, setParentalConsentRequired] = useState(false);
    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
            // The age must also be updated here, to ensure field validation errors are up to date.
            // Using viewmodel would leave the value 1 re-render behind
            if (draft.birthDate !== viewModel.birthDate) {
                const ageInYears = usersAgeInYears(draft.birthDate);
                if (Number.isNaN(ageInYears) || ageInYears >= parentalConsentMinAge) {
                    setParentalConsentRequired(false);
                    draft.metadata.parentalConsent = true;
                } else {
                    setParentalConsentRequired(true);
                    draft.metadata.parentalConsent = false;
                }
            }
        });

        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(
            viewModel,
            (draft: StudentPersonalInfoViewModel) => {
                draft.localeChoice = localeChoice;
            },
        );
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            {failedInstantMatch ? (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name & Birthdate"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and birthdate must match what is on your official school records."
                        />
                    </div>
                </div>
            ) : (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Student-Only Offer"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a student at a degree-granting college or university."
                        />
                         &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            )}

            {localeChoices.length > 1 ? (
                <ChangeLocaleComponent
                    options={localeChoices}
                    value={viewModel.localeChoice || defaultLocaleChoice}
                    isErrored={false}
                    onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                />
            ) : null}

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
            />

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={(choice: Organization) => {
                    updateViewModelOrganization(choice, verificationService);
                    updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                }}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue: string) => {
                        updateStudentViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue: string) => {
                        updateStudentViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            <BirthDate
                value={viewModel.birthDate}
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                )}
                onChange={(newValue: string) => {
                    updateStudentViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            {verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ? (
                <PhoneNumber
                    isRequired={!!verificationService.programTheme.smsLoopEnabled}
                    value={viewModel.phoneNumber}
                    isErrored={!!fieldValidationErrors.phoneNumber}
                    onChange={(newValue: string) => {
                        updateStudentViewModel('phoneNumber', newValue);
                        updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                    }}
                />
            ) : null}

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: string) => {
                    updateStudentViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />
            { parentalConsentRequired
                ? (
                    <ParentalConsentComponent
                        isRequired={parentalConsentRequired}
                        verificationService={verificationService}
                        isErrored={!!fieldValidationErrors.parentalConsent}
                        value={viewModel.metadata.parentalConsent}
                        onChange={(newValue: boolean) => {
                            updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, parentalConsent: newValue });
                            updateFieldValidationErrorsByFieldId('parentalConsent', newValue, verificationService);
                        }}
                    />
                )
                : null}
            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(
                            viewModel,
                            verificationService,
                            VerificationStepsEnum.collectStudentPersonalInfo,
                        )}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${
                            !isFormFilled(viewModel, verificationService.formValidationOptions)
                                ? 'sid-btn--disabled-like' : ''
                        }`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            {failedInstantMatch ? (
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            ) : (
                                <FormattedHTMLMessage
                                    id="verifyMyStudentStatus"
                                    defaultMessage="Verify My Student Status"
                                />
                            )}
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
